import { useParams, useNavigate } from 'react-router-dom';
import { Fragment, useEffect, useState } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { PageLayout } from '../../PageLayout';
import { PageCard } from '../../Compounds/CardWithTitle';
import {
  Ticket,
  deleteTicketById,
  getTicketById,
  getTicketSubject,
  handleInfoValidation,
  updateTicket
} from '../../../store/Ticket';
import { LabelValueField } from '../../Compounds/DetailPanel/LabelValueField';
import { getFormattedDate } from '../../../utils/helpers';
import { RunTotals } from '../../Compounds/RunTotal/RunTotals';
import { References } from './References';
import { ticketSections } from './ticketHelpers';
import { fetchUsersByRole, isData, isObserver, UserRole } from '../../../store/User';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { ETUMiniTable } from '../../Compounds/ETU/ETUMiniTable';
import { fetchETUs, getETULoadingSubject, getETUSubject, removeETU } from '../../../store/ETU';
import { fetchTicketTFUs, getTFULoadingSubject, getTFUSubject, removeTFU } from '../../../store/TFU';
import { TFUMiniTable } from '../../Compounds/TFU/TFUMiniTable';
import { DetailsComments } from '../../Compounds/DetailsComments';
import { DetailsAttachments } from '../../Compounds/DetailsAttachments';
import { ConfirmationModal } from '../../Atoms';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { DetailViewValidationCard, ValidatorResult } from '../../Compounds/CardWithTitle';
import { formatValidationMessages, handleRunValidator } from '../../Compounds/CardWithTitle/validationCardHelpers';
import { getSelectedTab, updateState } from '../../../store/GeneralStore';
import { TabPanel } from '../../Atoms/TabPanel';
import { HistoryMiniTable } from '../../Compounds/History/HistoryTable';
import {
  fetchChangeHistory,
  getChangeHistoryLoadingSubject,
  getChangeHistorySubject
} from '../../../store/ChangeHistory';

const tabs = ['Info', 'History'];

export const TicketDetailPage = () => {
  const [ticketData, setTicketData] = useState<Ticket>({} as Ticket);
  const [loading, setLoading] = useState<boolean>(true);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const [validationStatusBySection, setValidationStatusBySection] = useState<Record<string, ValidatorResult>>({});
  const [tabIndex, setTabIndex] = useState<number>(
    getSelectedTab().ticketDetail ? Number(getSelectedTab().ticketDetail) : 0
  );
  const { setSnack } = useSnackbar();

  const handleTabChange = (tabIndex: number) => {
    setTabIndex(tabIndex);
    updateState({
      selectedTab: {
        ...getSelectedTab(),
        ticketDetail: tabIndex
      }
    });
  };

  const getData = async () => {
    if (id) {
      setLoading(true);
      const errorStatusCode = await getTicketById(id);
      if (errorStatusCode) navigate(`/error/${errorStatusCode}/ticket/${id}/${new Date().getTime()}`);
      await fetchUsersByRole([UserRole.ADMIN, UserRole.ANALYST]);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) getData();
  }, [id]);

  useEffect(() => {
    const ticketSub = getTicketSubject().subscribe((ticket) => {
      setTicketData(ticket);
    });
    getData();

    return () => {
      if (ticketSub) ticketSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const messages = formatValidationMessages(validationStatusBySection);
    if (messages.length) {
      setSnack({
        message: messages.join(', '),
        type: 'warning',
        duration: 15000,
        open: true
      });
    }
  }, [JSON.stringify(validationStatusBySection)]);

  const deleteButton = () => {
    if (isObserver() || isData()) return <></>;

    const handleRemove = async () => {
      const data = await deleteTicketById(id as string);
      if (data) {
        setSnack({ message: 'Ticket has been deleted', type: 'success', open: true });
        navigate('/ticket');
      }
    };

    return (
      <div className="px-4 py-2">
        <div className="w-full border-t border-grey-2 my-3" />
        <button
          className="text-sm text-red rounded my-2 py-2 px-4 bg-[#FFEFEF] hover:bg-[#FDD3D3]"
          onClick={() => setOpenConfirmation(true)}>
          <DeleteIcon
            color="red"
            className="mr-2 -mt-1"
            fontSize="small"
          />
          <span>Delete Ticket</span>
        </button>
        <ConfirmationModal
          open={openConfirmation}
          setOpen={setOpenConfirmation}
          handleSubmit={handleRemove}
          text="Are you sure you want to permanently delete this Ticket?"
          confirmButtonText="Delete"
        />
      </div>
    );
  };

  let title = [ticketData?.number, ticketData?.supplierName, ticketData?.tsdName].filter(Boolean).join(' | ');
  if (
    ticketData?.ticketType === 'Assets and RPM Accounts' &&
    ticketData.customerList &&
    ticketData.customerList.length > 0
  ) {
    title = `${title} | ${ticketData.customerList?.[0].name}`;
  }

  const tabContent = () => {
    switch (tabIndex) {
      case 0:
        return (
          <div className="w-full flex mt-3 space-x-6">
            <div className="w-8/12 space-y-6">
              <RunTotals ticketId={ticketData?.id} />
              <TFUMiniTable
                id={id as string}
                fetchFunction={() => fetchTicketTFUs(id as string)}
                dataSubject={getTFUSubject}
                loadingSubject={getTFULoadingSubject}
                removeFunction={removeTFU}
                ticketStatus={ticketData?.status}
              />
              <ETUMiniTable
                id={id as string}
                fetchFunction={() => fetchETUs({ ticket: id })}
                dataSubject={getETUSubject}
                loadingSubject={getETULoadingSubject}
                removeFunction={removeETU}
              />
              <References ticket={ticketData} />
            </div>
            <div className="w-4/12 self-start space-y-6">
              <DetailViewValidationCard
                data={ticketSections(ticketData)}
                detailValidator={(t) =>
                  handleRunValidator(
                    'info',
                    t,
                    handleInfoValidation,
                    validationStatusBySection,
                    setValidationStatusBySection
                  )
                }
                enableCollapse
                key="ticket-validation-card"
                saveAction={(t) => updateTicket(t as Ticket)}
                title="Info"
              />
              <DetailsComments
                id={ticketData?.id}
                entityType="ticket"
              />
              {id && (
                <DetailsAttachments
                  id={id}
                  entityType="ticket"
                />
              )}
              <PageCard
                dataTestId="history-panel"
                title="Meta">
                <div className="flex px-4 pt-4 w-full">
                  <LabelValueField
                    label="Created Date"
                    value={getFormattedDate(ticketData.createdAt, 'lll')}
                    field="createdAt"
                  />
                  <LabelValueField
                    label="Modified Date"
                    value={getFormattedDate(ticketData.updatedAt, 'lll')}
                    field="updatedAt"
                  />
                </div>
                <div className="flex px-4 pt-4 w-full">
                  <span className="w-1/2 flex-col"></span>
                  <LabelValueField
                    label="Status Date"
                    value={getFormattedDate(ticketData.statusDate, 'lll')}
                    field="statusDate"
                  />
                </div>
                {deleteButton()}
              </PageCard>
            </div>
          </div>
        );
      case 1:
        return (
          <HistoryMiniTable
            id={id as string}
            fetchFunction={fetchChangeHistory}
            dataSubject={getChangeHistorySubject}
            loadingSubject={getChangeHistoryLoadingSubject}
            recordType="ticket"
          />
        );
      default:
        return null;
    }
  };

  return (
    <PageLayout
      className="mb-10"
      dataTestId="Ticket-detail-page"
      subTitle="Ticket"
      pageTitle={loading ? 'Loading...' : title}
      showIcon={true}
      showCopy={true}
      copyValue={title}
      key={ticketData?.id}
      icon="ticketMed"
      tabs={tabs}
      handleTabChange={handleTabChange}
      activeTab={tabIndex}>
      <Fragment>
        {loading && <ComponentSpinner sx={{ height: '35rem', margin: 'auto' }} />}
        {!loading && (
          <div className="mt-6">
            {tabs.map((tab, index) => (
              <TabPanel
                key={tab}
                value={tabIndex}
                index={index}
                boxPaddingTop={0}>
                {index === tabIndex ? tabContent() : null}
              </TabPanel>
            ))}
          </div>
        )}
      </Fragment>
    </PageLayout>
  );
};
